import { AsyncPipe, NgClass } from '@angular/common';
import { Component, computed, DestroyRef, effect, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { LoaderService } from '@services/loader.service';
import { NotificationsService } from '@services/notifications.service';
import { PaypalService } from '@services/paypal.service';
import { TimezoneService } from '@services/timezone.service';
import { UserService } from '@services/user.service';
import { PENDING_PAYPAL_CONFIRM_STORAGE_KEY } from './user/paypal-payment/transaction.helper';
import { ModalComponent } from '@shared/elements/modal/modal.component';
import { NotificationComponent } from '@shared/elements/notification/notification.component';
import { SpinnerComponent } from '@shared/elements/spinner/spinner.component';
import { convertToTitleCase, GridCellActions } from '@shared/grid-helper';
import {
  getSessionDurationInMinutes,
  isSessionValid,
  parseIAT,
  saveMandatoryUserInfoToStorage,
} from '@shared/helpers/system.helper';
import { UserPrimaryInfo } from '@shared/models/model';
import { distinctUntilChanged, tap } from 'rxjs';
import { SEO_CONFIG } from '../assets/seo/seo-config';
import { environment } from '../environments/environment';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { SideBarComponent } from './components/side-bar/side-bar.component';
import { LocalStorageService } from './services/local-storage.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    NgClass,
    AsyncPipe,
    RouterOutlet,
    HeaderComponent,
    SideBarComponent,
    FooterComponent,
    NotificationComponent,
    ModalComponent,
    SpinnerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  readonly loaderService = inject(LoaderService);
  readonly storage = inject(LocalStorageService);
  readonly notificationService = inject(NotificationsService);
  readonly router = inject(Router);

  private readonly idle = inject(Idle);
  private readonly keepalive = inject(Keepalive);
  private readonly swUpdate = inject(SwUpdate);
  private readonly userService = inject(UserService);
  private readonly paypalService = inject(PaypalService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly destroyRef = inject(DestroyRef);

  readonly loggedIn = computed(() => this.storage.isLoggedIn());
  readonly isAdmin = computed(
    () => ['SUPER_ADMIN', 'DOMAIN_ADMIN'].findIndex((i) => i === this.storage.userInfo()?.userRole?.name) > -1,
  );
  readonly isPreviewMode = computed(() => this.storage.previewUserScreens());
  readonly userInfo = computed(() => this.storage.userInfo());

  readonly updateReady = signal(false);
  readonly showUpdateBanner = signal(false);

  private lastPing?: Date;
  private callInProgress = false;
  private pendingPaypalConfirmRecoveryAttempted = false;
  showHeader = true;
  tokenConsentId?: number;

  @ViewChild('mainContent') mainContentRef!: ElementRef<HTMLElement>;

  private refreshTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.setBaseInfo();
    this.storage.changeLoggedIn(!!localStorage.getItem('jwt'));

    effect(() => {
      if (this.isAdmin() && this.router.url === '/') {
        this.router.navigate(['/admin/dashboard']);
      }

      const jwt = localStorage.getItem('jwt');
      const userID = localStorage.getItem('userId');

      if (this.loggedIn() && jwt && userID) {
        this.storage.sessionSubject.next(jwt);
        this.checkJWT();
        this.recoverPendingPaypalConfirmation();

        if (!this.userInfo() && !this.callInProgress) {
          this.userService
            .getUserCustomFields(userID)
            .pipe(tap(() => (this.callInProgress = true)))
            .subscribe((res) => {
              if (res.data.userInfo) {
                this.storage.userInfo.set(res.data.userInfo as UserPrimaryInfo);
              }
              this.callInProgress = false;
            });
        }
      }
    });

    this.keepAliveDependingOnIdleStatus();

    this.notificationService
      .getObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: any) => {
        if (response && this.tokenConsentId === response?.index && response.postAction === GridCellActions.CONFIRM) {
          this.refreshToken();
        }
      });

    this.storage.session$
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((jwt) => this.handleTimerForRefresh(jwt));

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.showHeader =
          event.url !== '/login' && event.url !== '/registration' && !event.url.includes('accountverify');
        this.checkJWT();

        if (this.updateReady()) {
          this.swUpdate
            .activateUpdate()
            .then(() => {
              window.location.href = event.url;
            })
            .catch(() => window.location.reload());
          return;
        }
      }

      if (event instanceof NavigationEnd) {
        if (this.mainContentRef?.nativeElement) {
          this.mainContentRef.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    });

    if (this.swUpdate.isEnabled) {
      this.checkForUpdates();
      this.swUpdate.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((version: VersionEvent) => {
        if (version.type === 'VERSION_DETECTED') {
          console.log('[ServiceWorker] New version detected. Downloading in background...', new Date());
        }

        if (version.type === 'VERSION_READY') {
          console.log('[ServiceWorker] New version ready.', new Date());
          this.updateReady.set(true);
          this.showUpdateBanner.set(true);
        }

        if (version.type === 'NO_NEW_VERSION_DETECTED') {
          console.log('[ServiceWorker] All up-to-date!', new Date());
        }
      });
    }

    this.destroyRef.onDestroy(() => {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
      }
    });

    if (environment.production) {
      console.log('[ServiceWorker] enabled');
    } else {
      console.log('You are running in development mode.');
    }
  }

  handleTimerForRefresh(jwt: string | null): void {
    if (jwt) {
      const iat = parseIAT(jwt);

      if (iat) {
        const timePassed = getSessionDurationInMinutes(Number(iat) * 1000);

        if (timePassed >= 55 && timePassed < 60) {
          setTimeout(() => this.promptTokenRefresh(), 0);
        } else if (timePassed < 56) {
          const minutesBefore = 55 - timePassed;
          this.refreshTimer = setTimeout(() => this.promptTokenRefresh(), minutesBefore * 60000);
        }
      }
    } else {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
      }
    }
  }

  refreshToken() {
    this.userService.refreshToken({ id: localStorage.getItem('userId')! }).subscribe({
      next: (res) => {
        const info = res.data;

        if (info?.jwt) {
          saveMandatoryUserInfoToStorage(info);
          this.storage.sessionSubject.next(info.jwt);
        }
      },
      error: () => this.logout(),
    });
  }

  checkJWT() {
    const jwt = localStorage.getItem('jwt');
    this.storage.sessionSubject.next(jwt);

    if (jwt) {
      const time = parseIAT(jwt);

      if (Number(time)) {
        const timeInMiliseconds = Number(time) * 1000;

        if (!isSessionValid(timeInMiliseconds)) {
          this.storage.changeLoggedIn(false);
          this.notificationService.warning('Session Closed', 'Session is expired. Please log in again', 4000);
          this.router.navigate(['/login']);
        }
      }
    }
  }

  // Bug F3 fix: recovers a confirmTransaction() call that was persisted by PaypalPaymentComponent
  // but never got a response - e.g. the tab was closed or the network dropped right after a
  // successful PayPal capture. Runs once per app load; PaypalPaymentComponent itself removes the
  // localStorage entry as soon as a confirm call actually succeeds.
  recoverPendingPaypalConfirmation() {
    if (this.pendingPaypalConfirmRecoveryAttempted) {
      return;
    }
    this.pendingPaypalConfirmRecoveryAttempted = true;

    const pending = localStorage.getItem(PENDING_PAYPAL_CONFIRM_STORAGE_KEY);
    if (!pending) {
      return;
    }

    try {
      const params = JSON.parse(pending);
      this.paypalService.confirmTransaction(params).subscribe({
        next: () => {
          localStorage.removeItem(PENDING_PAYPAL_CONFIRM_STORAGE_KEY);
          this.notificationService.success('Payment Confirmed', 'Your previous payment has been confirmed.', 6000);
        },
        error: () => {
          // Leave it in localStorage - will retry on the next app load. Deliberately silent here
          // so the user isn't shown a repeated error every time they open the app while we retry.
        },
      });
    } catch {
      // Malformed entry (shouldn't happen since we always JSON.stringify it ourselves) - drop it
      // rather than retrying forever.
      localStorage.removeItem(PENDING_PAYPAL_CONFIRM_STORAGE_KEY);
    }
  }

  promptTokenRefresh() {
    this.tokenConsentId = this.notificationService.tokenExpiration(
      'Session Expiring',
      'Your session is soon to be closed. Would you like to refresh it to continue working?',
      55000,
    );
  }

  keepAliveDependingOnIdleStatus() {
    const LIMIT_IN_SECONDS = 59 * 60;
    this.idle.setIdle(LIMIT_IN_SECONDS);
    this.idle.setTimeout(10);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleEnd.subscribe();
    this.idle.onTimeout.subscribe(() => {
      this.logout();
      this.notificationService.warning('Session Closed', 'You have been idle too long. Please log in again', 10000);
      this.router.navigate(['/login']);
    });
    this.idle.onIdleStart.subscribe(() => {
      if (this.updateReady()) {
        this.applyUpdate();
      }
    });

    this.keepalive.interval(30);
    this.keepalive.onPing.subscribe(() => (this.lastPing = new Date()));
    this.reset();
  }

  applyUpdate() {
    this.swUpdate
      .activateUpdate()
      .then(() => window.location.reload())
      .catch((error) => console.error('[ServiceWorker] Error during update activation:', error, new Date()));
  }

  dismissUpdate() {
    this.showUpdateBanner.set(false);
  }

  checkForUpdates(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate
        .checkForUpdate()
        .then(() => console.log('[ServiceWorker] Checked for updates', new Date()))
        .catch((err) => console.error('[ServiceWorker] Error checking for updates:', err, new Date()));
    }
  }

  reset() {
    this.idle.watch();
  }

  logout() {
    const sessionId = Number(localStorage.getItem('SessionId'));
    const data = { id: sessionId, logDesc: false };

    this.userService.logout(data).subscribe({
      complete: () => {
        this.storage.changeLoggedIn(false);
        this.showHeader = false;
        this.router.navigate(['/login']);
      },
    });
  }

  setBaseInfo() {
    const subdomain = this.getSubdomain();
    const show = subdomain === 'mockingbird' ? 'bluebird' : subdomain;
    this.userService.setDomainName(show);
    this.timezoneService.init();
    const seo = SEO_CONFIG[subdomain];

    if (seo) {
      this.meta.updateTag({ name: 'title', content: seo.title });
      this.meta.updateTag({ name: 'description', content: seo.description });
    }

    this.title.setTitle(`${convertToTitleCase(subdomain)} | Squarehubs`);
    this.userService.getSiteInfo().subscribe((res) => {
      this.storage.logoId.set(res.data.siteLogo.id);
    });

    const theme = localStorage.getItem('theme') as 'dark' | 'light';

    if (theme) {
      document.documentElement.classList.add(`theme-${theme}`);
      this.storage.theme.set(theme);
    }
  }

  getSubdomain(): string {
    const host = window.location.hostname;
    const parts = host.split('.');

    if (parts.length < 3) return '';

    return parts[0];
  }
}
